import React, { useEffect, useMemo, useState } from "react";
import axios from "api/axios";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import PageShell, { SectionCard } from "components/patch/PageShell";
import SimpleTable from "components/patch/SimpleTable";

const money = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function PayrollEnterpriseControls() {
  const currentMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);
  const [month, setMonth] = useState(currentMonth);
  const [runId, setRunId] = useState("");
  const [reason, setReason] = useState("");
  const [runs, setRuns] = useState([]);
  const [locks, setLocks] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [reversals, setReversals] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const selectedRunId = runId || runs?.[0]?.id || "";

  const load = async () => {
    setLoading(true);
    setMessage("");
    try {
      const [runsRes, locksRes, reversalsRes, auditRes] = await Promise.all([
        axios.get("/payroll-run", { params: { month, per_page: 20 } }),
        axios.get("/payroll-controls/locks", { params: { month, per_page: 20 } }),
        axios.get("/payroll-controls/reversals", { params: { per_page: 20 } }),
        axios.get("/payroll-controls/audit", { params: { per_page: 20 } }),
      ]);
      const runRows = runsRes.data?.data?.data || runsRes.data?.data || [];
      setRuns(Array.isArray(runRows) ? runRows : []);
      if (!runId && runRows?.[0]?.id) setRunId(String(runRows[0].id));
      setLocks(locksRes.data?.data?.data || locksRes.data?.data || []);
      setReversals(reversalsRes.data?.data?.data || reversalsRes.data?.data || []);
      setAudit(auditRes.data?.data?.data || auditRes.data?.data || []);
    } catch (e) {
      setMessage(e.response?.data?.message || "Unable to load enterprise controls.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const lockPeriod = async () => {
    if (!reason.trim()) return setMessage("Enter a reason before locking/unlocking.");
    setLoading(true);
    try {
      await axios.post("/payroll-controls/lock", { month, reason });
      setReason("");
      setMessage("Payroll period locked successfully.");
      await load();
    } catch (e) {
      setMessage(e.response?.data?.message || "Unable to lock period.");
    } finally {
      setLoading(false);
    }
  };

  const unlockPeriod = async () => {
    if (!reason.trim()) return setMessage("Enter a reason before unlocking.");
    setLoading(true);
    try {
      await axios.post("/payroll-controls/unlock", { month, reason });
      setReason("");
      setMessage("Payroll period unlocked successfully.");
      await load();
    } catch (e) {
      setMessage(e.response?.data?.message || "Unable to unlock period.");
    } finally {
      setLoading(false);
    }
  };

  const checkReadiness = async () => {
    if (!selectedRunId) return setMessage("Select a payroll run first.");
    setLoading(true);
    try {
      const res = await axios.get(`/payroll-controls/runs/${selectedRunId}/readiness`);
      setReadiness(res.data?.data || null);
      setMessage(
        res.data?.data?.passed
          ? "Payroll run passed enterprise readiness checks."
          : "Payroll run has unresolved readiness issues."
      );
    } catch (e) {
      setMessage(e.response?.data?.message || "Unable to check readiness.");
    } finally {
      setLoading(false);
    }
  };

  const reconcile = async () => {
    if (!selectedRunId) return setMessage("Select a payroll run first.");
    setLoading(true);
    try {
      const res = await axios.post(`/payroll-controls/runs/${selectedRunId}/reconcile`);
      setReconciliation(res.data?.data || null);
      setMessage(res.data?.message || "Reconciliation completed.");
    } catch (e) {
      setMessage(e.response?.data?.message || "Unable to reconcile payroll.");
    } finally {
      setLoading(false);
    }
  };

  const requestReversal = async () => {
    if (!selectedRunId) return setMessage("Select a payroll run first.");
    if (!reason.trim()) return setMessage("Enter reversal reason.");
    setLoading(true);
    try {
      await axios.post(`/payroll-controls/runs/${selectedRunId}/reversal/request`, { reason });
      setReason("");
      setMessage("Reversal request submitted for approval.");
      await load();
    } catch (e) {
      setMessage(e.response?.data?.message || "Unable to request reversal.");
    } finally {
      setLoading(false);
    }
  };

  const approveReversal = async (id) => {
    setLoading(true);
    try {
      await axios.post(`/payroll-controls/reversals/${id}/approve`, {
        approval_note: "Approved from Enterprise Controls",
      });
      await load();
    } catch (e) {
      setMessage(e.response?.data?.message || "Unable to approve reversal.");
    } finally {
      setLoading(false);
    }
  };

  const applyReversal = async (id) => {
    setLoading(true);
    try {
      await axios.post(`/payroll-controls/reversals/${id}/apply`);
      await load();
    } catch (e) {
      setMessage(e.response?.data?.message || "Unable to apply reversal.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      title="Payroll Enterprise Controls"
      subtitle="Period lock, readiness checks, reconciliation, reversal and payroll audit controls."
    >
      {message && (
        <SectionCard>
          <MDTypography
            variant="button"
            color={
              message.toLowerCase().includes("unable") || message.toLowerCase().includes("issue")
                ? "error"
                : "success"
            }
          >
            {message}
          </MDTypography>
        </SectionCard>
      )}

      <SectionCard title="Control Panel">
        <MDBox
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 2fr" }}
          gap={2}
          alignItems="center"
        >
          <MDInput
            type="month"
            label="Payroll Month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
          <select
            value={selectedRunId}
            onChange={(e) => setRunId(e.target.value)}
            style={{ height: 44, borderRadius: 8, border: "1px solid #ddd", padding: "0 12px" }}
          >
            <option value="">Select Payroll Run</option>
            {runs.map((run) => (
              <option key={run.id} value={run.id}>
                #{run.id} - {run.month} - {run.status}
              </option>
            ))}
          </select>
          <MDInput
            label="Reason / Approval Note"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </MDBox>
        <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
          <MDButton color="info" disabled={loading} onClick={load}>
            Refresh
          </MDButton>
          <MDButton color="warning" disabled={loading} onClick={lockPeriod}>
            Lock Period
          </MDButton>
          <MDButton color="secondary" disabled={loading} onClick={unlockPeriod}>
            Unlock Period
          </MDButton>
          <MDButton color="dark" disabled={loading} onClick={checkReadiness}>
            Check Finalization Readiness
          </MDButton>
          <MDButton color="success" disabled={loading} onClick={reconcile}>
            Run Reconciliation
          </MDButton>
          <MDButton color="error" disabled={loading} onClick={requestReversal}>
            Request Reversal
          </MDButton>
        </MDBox>
      </SectionCard>

      {readiness && (
        <SectionCard title="Finalization Readiness">
          <MDTypography variant="h6" color={readiness.passed ? "success" : "error"}>
            {readiness.passed ? "PASSED" : "BLOCKED"}
          </MDTypography>
          <SimpleTable
            columns={["Code", "Severity", "Status", "Message"]}
            rows={(readiness.checks || []).map((c) => [
              c.code,
              c.severity,
              c.passed ? "Passed" : "Failed",
              c.message,
            ])}
          />
        </SectionCard>
      )}

      {reconciliation && (
        <SectionCard title="Payroll Reconciliation">
          <SimpleTable
            columns={[
              "Staff",
              "Gross",
              "Deductions",
              "Net",
              "Tax",
              "Employee Pension",
              "Employer Pension",
              "NHF",
              "Difference",
              "Status",
            ]}
            rows={[
              [
                reconciliation.staff_count,
                money(reconciliation.total_gross),
                money(reconciliation.total_deductions),
                money(reconciliation.total_net),
                money(reconciliation.total_tax),
                money(reconciliation.total_employee_pension),
                money(reconciliation.total_employer_pension),
                money(reconciliation.total_nhf),
                money(reconciliation.control_difference),
                reconciliation.status,
              ],
            ]}
          />
        </SectionCard>
      )}

      <SectionCard title="Period Locks">
        <SimpleTable
          columns={["Month", "Status", "Reason", "Locked At", "Unlocked At"]}
          rows={locks.map((r) => [
            r.month,
            r.status,
            r.reason,
            r.locked_at || "-",
            r.unlocked_at || "-",
          ])}
        />
      </SectionCard>

      <SectionCard title="Reversal Requests">
        <SimpleTable
          columns={["Run", "Month", "Reason", "Status", "Actions"]}
          rows={reversals.map((r) => [
            r.payroll_run_id,
            r.month,
            r.reason,
            r.status,
            <MDBox display="flex" gap={1} key={r.id}>
              {r.status === "pending" && (
                <MDButton size="small" color="success" onClick={() => approveReversal(r.id)}>
                  Approve
                </MDButton>
              )}
              {r.status === "approved" && (
                <MDButton size="small" color="error" onClick={() => applyReversal(r.id)}>
                  Apply
                </MDButton>
              )}
            </MDBox>,
          ])}
        />
      </SectionCard>

      <SectionCard title="Recent Payroll Audit Events">
        <SimpleTable
          columns={["Event", "Description", "User", "Date"]}
          rows={audit.map((r) => [
            r.event_type,
            r.description,
            r.user_id || "system",
            r.created_at,
          ])}
        />
      </SectionCard>
    </PageShell>
  );
}
